import React, { useState, useEffect } from "react";
import { Flex, Text, Button, Box } from "theme-ui";
import { ArrowLeft } from "react-feather";
import { useBreakpointIndex } from "@theme-ui/match-media";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from "react-i18next";

import { Input, Modal } from "src/components/atoms";
import { ContactFormProps } from "./ContactForm.d";

const MAX_MSG = 500;

const CategoriesSelection: React.FC<{
  reason?: string;
  setReason?: (reason: string) => void;
  onBack?: () => void;
}> = ({ reason, setReason, onBack }) => {
  const { t } = useTranslation();

  const valuesContact: string[] = t("contact.form.reasons", {returnObjects: true});

  return (
    <>
      <Button
        onClick={onBack}
        sx={{
          position: "absolute",
          top: "20px",
          left: "10px",
          color: "secondary",
          display: ["block", "none"],
        }}
        variant="icon"
      >
        <ArrowLeft />
      </Button>
      <Flex
        sx={{
          width: ["100vw", "fit-content"],
          height: ["100vh", "fit-content"],
          bg: "background",
          flexDirection: "column",
          justifyContent: "center",
          py: 5,
        }}
      >
        <Text
          sx={{
            p: [4, 5],
            fontSize: 4,
            mb: [5, 2],
            textAlign: "center",
            fontWeight: "bold",
          }}
        >
          {t("contact.form.reason_label")}
        </Text>
        {valuesContact.map((r) => (
          <Flex
            key={r}
            onClick={() => setReason?.(r)}
            sx={{
              py: 4,
              px: [4, 5],
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "5px solid rgba(0, 0, 0, 0.05)",
              cursor: "pointer",
            }}
          >
            <Text sx={{marginRight: 5}}>{r}</Text>
            <input
              type="radio"
              onChange={() => setReason?.(r)}
              checked={r === reason}
            />
          </Flex>
        ))}
      </Flex>
    </>
  );
};
export const ContactForm: React.FC<ContactFormProps> = ({
  reset,
  onSubmit,
}) => {
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    reason: "",
    message: "",
  });
  const [isOpen, setIsOpen] = useState(false);

  const { t } = useTranslation();

  const [captchaValid, setCaptchaValid] = useState(false);

  const index = useBreakpointIndex();

  const messageValid =
    contactForm.message && contactForm.message.length <= MAX_MSG;

  const emailValid = contactForm.email.match(
    /^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i
  );

  const messageCountDown = MAX_MSG - contactForm.message.length;

  const isValid =
    contactForm.name &&
    contactForm.email &&
    contactForm.reason &&
    messageValid &&
    emailValid &&
    captchaValid;

  const resetForm = () => {
    setContactForm({
      name: "",
      email: "",
      reason: "",
      message: "",
    });
  };

  const captchaChange = (value: string | null) => {
    setCaptchaValid(!!value);
  };

  const handleOnSubmit = async (e: React.FormEvent<HTMLDivElement>) => {
    e.preventDefault();

    if (isValid) {
      onSubmit(contactForm);
    }
  };

  useEffect(() => {
    if (reset) {
      resetForm();
    }
  }, [reset]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        borderRadius={index > 0 ? "20px" : "0"}
      >
        <CategoriesSelection
          reason={contactForm.reason}
          setReason={(reason) => {
            setContactForm({ ...contactForm, reason });
            setIsOpen(false);
          }}
          onBack={() => setIsOpen(false)}
        />
      </Modal>
      <Flex
        as="form"
        onSubmit={handleOnSubmit}
        sx={{
          bg: "white",
          p: [5, 6],
          gap: [4, 5],
          flexDirection: "column",
        }}
      >
        <Flex
          sx={{
            gap: [4, 5],
            width: "100%",
            flexDirection: ["column", "column", "row"],
          }}
        >
          <Input
            onValueChange={(val) =>
              setContactForm({ ...contactForm, name: val })
            }
            label={t("contact.form.name")}
            type="text"
            value={contactForm.name}
          />
          <Input
            onValueChange={(val) =>
              setContactForm({ ...contactForm, email: val })
            }
            label={t("contact.form.email")}
            type="text"
            value={contactForm.email}
            error={contactForm.email && !emailValid ? t("contact.form.email_error") : ""}
          />
        </Flex>
        <Input
          onClick={() => setIsOpen(true)}
          label={contactForm.reason ? t("contact.form.reason.1") : t("contact.form.reason.0")}
          type="select"
          value={contactForm.reason}
        />
        <Input
          onValueChange={(val) =>
            setContactForm({ ...contactForm, message: val })
          }
          label={!!contactForm.message ? t("contact.form.message.1") : t("contact.form.message.0")}
          type="textArea"
          value={contactForm.message}
          rows={5}
          error={contactForm.message.length > MAX_MSG ? t("contact.form.message_error") : ""}
          message={messageCountDown > 0 ? `${messageCountDown}` : "0"}
        />
        <Box sx={{ m: "0 auto", mt: 5 }}>
          <ReCAPTCHA
            sitekey={process.env.REACT_APP_CAPTCHA_KEY!}
            onChange={captchaChange}
          />
        </Box>
        <Button
          type="submit"
          sx={{
            margin: "0 auto",
            px: [4, 5, 8],
            mt: [3, 4, 4],
            opacity: isValid ? 1 : 0.4,
            bg: isValid ? "secondary" : "primary",
          }}
        >
          {t("btns.send")}
        </Button>
      </Flex>
    </>
  );
};
